import { useCallback, useEffect, useMemo, useState } from "react";
import { ExternalLink, FilePenLine, MessageSquare, Sparkles } from "lucide-react";
import AivoryOnePanel from "@/components/AivoryOnePanel";
import OrchestratorLiveStage from "@/components/OrchestratorLiveStage";
import { listConversations } from "@/lib/aivory-one-api";
import { useEventMeta } from "@/lib/event-meta";

const TABS = ["preview", "event", "registration", "comms", "schedule"];

function tabForArtifact(type = "") {
  if (["live_page", "page_draft", "event_created", "event_updated"].includes(type)) return "event";
  if (["form_draft", "form_published", "ticket_tier", "track"].includes(type)) return "registration";
  if (["campaign_draft", "template"].includes(type)) return "comms";
  if (["session", "speaker"].includes(type)) return "schedule";
  return "preview";
}

function ArtifactCard({ artifact }) {
  if (!artifact) return null;
  return (
    <div className="rounded-xl border border-[var(--border-default)] bg-[var(--bg-elev)] p-3 space-y-2">
      <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-tertiary)]">{artifact.type || "artifact"}</div>
      <div className="text-sm font-medium text-[var(--text-primary)]">{artifact.label || "Update"}</div>
      <div className="flex flex-wrap gap-2">
        {artifact.publicUrl ? (
          <a
            href={artifact.publicUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 rounded-lg border border-[var(--primary)] px-2.5 py-1 text-[11px] text-[var(--primary)]"
          >
            <ExternalLink size={12} /> Live page
          </a>
        ) : null}
        {artifact.adminUrl ? (
          <a
            href={artifact.adminUrl}
            className="inline-flex items-center gap-1 rounded-lg border border-[var(--border-default)] px-2.5 py-1 text-[11px] text-[var(--text-secondary)]"
          >
            <FilePenLine size={12} /> Open
          </a>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Full AivoryONE workspace shell: conversation rail + chat + live workspace tabs.
 */
export default function AivoryOneWorkspace({ aiEnabled = true, eventId = null, onClose }) {
  const [conversations, setConversations] = useState([]);
  const [liveArtifact, setLiveArtifact] = useState(null);
  const [artifacts, setArtifacts] = useState([]);
  const [activeEventId, setActiveEventId] = useState(eventId);
  const [workspaceTab, setWorkspaceTab] = useState("preview");
  const eventMeta = useEventMeta(activeEventId || eventId);

  useEffect(() => {
    if (!aiEnabled) return;
    listConversations().then(setConversations).catch(() => setConversations([]));
  }, [aiEnabled]);

  // Seed Preview from the current event so the rail isn't blank before the next action.
  useEffect(() => {
    if (liveArtifact?.publicUrl || liveArtifact?.previewUrl) return;
    const slug = String(eventMeta?.slug || "").trim();
    const eid = activeEventId || eventId;
    if (!slug || !eid) return;
    setLiveArtifact({
      type: "live_page",
      label: eventMeta?.title || "Event landing page",
      publicUrl: `${window.location.origin}/e/${slug}`,
      previewUrl: `${window.location.origin}/e/${slug}`,
      adminUrl: `/admin/events/${eid}/landing`,
      eventId: eid,
    });
  }, [activeEventId, eventId, eventMeta?.slug, eventMeta?.title, liveArtifact?.previewUrl, liveArtifact?.publicUrl]);

  const onWorkspaceUpdate = useCallback((update = {}) => {
    if (update.eventId) setActiveEventId(update.eventId);
    if (Array.isArray(update.artifacts) && update.artifacts.length) {
      setArtifacts((prev) => {
        const next = [...update.artifacts, ...prev];
        const seen = new Set();
        return next.filter((a) => {
          const key = `${a.type}|${a.label}|${a.adminUrl || a.publicUrl || ""}`;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        }).slice(0, 40);
      });
    }
    if (update.stageArtifact) {
      setLiveArtifact(update.stageArtifact);
      // Jump to preview when a page/iframe-capable artifact lands
      if (update.stageArtifact.publicUrl || update.stageArtifact.previewUrl) {
        setWorkspaceTab("preview");
      } else if (update.stageArtifact.type) {
        setWorkspaceTab(tabForArtifact(update.stageArtifact.type));
      }
    }
  }, []);

  const tabArtifacts = useMemo(() => {
    if (workspaceTab === "preview") return artifacts;
    return artifacts.filter((a) => tabForArtifact(a.type) === workspaceTab);
  }, [artifacts, workspaceTab]);

  const previewCapable = liveArtifact?.publicUrl || liveArtifact?.previewUrl;

  return (
    <div className="fixed inset-0 z-[70] flex bg-[var(--bg-base)]" data-testid="aivory-one-workspace">
      <aside className="w-56 shrink-0 border-r border-[var(--border-default)] bg-[var(--bg-surface)] flex flex-col">
        <div className="p-4 border-b border-[var(--border-default)] flex items-center gap-2">
          <Sparkles size={18} className="text-[var(--primary)]" />
          <span className="font-semibold text-sm">AivoryONE (Beta)</span>
        </div>
        <div className="flex-1 overflow-y-auto p-2 space-y-1">
          {conversations.map((c) => (
            <button key={c.id} type="button" className="w-full text-left rounded-lg px-3 py-2 text-xs hover:bg-[var(--bg-elev)] truncate flex items-center gap-2">
              <MessageSquare size={12} />
              {c.title || "Chat"}
            </button>
          ))}
        </div>
      </aside>

      <main className="flex-1 min-w-0 flex flex-col">
        <AivoryOnePanel
          variant="embedded"
          aiEnabled={aiEnabled}
          eventId={activeEventId || eventId}
          onClose={onClose}
          onWorkspaceUpdate={onWorkspaceUpdate}
        />
      </main>

      <aside className="w-[min(420px,40vw)] shrink-0 border-l border-[var(--border-default)] bg-[var(--bg-surface)] flex flex-col hidden lg:flex">
        <div className="flex border-b border-[var(--border-default)]">
          {TABS.map((tab) => (
            <button
              key={tab}
              type="button"
              className={`flex-1 py-2 text-[10px] font-mono uppercase ${workspaceTab === tab ? "border-b-2 border-[var(--primary)] text-[var(--primary)]" : "text-[var(--text-tertiary)]"}`}
              onClick={() => setWorkspaceTab(tab)}
            >
              {tab}
            </button>
          ))}
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto">
          {workspaceTab === "preview" && previewCapable ? (
            <div className="h-full flex flex-col">
              <div className="flex-1 min-h-[280px]">
                <OrchestratorLiveStage
                  artifact={liveArtifact}
                  onClose={() => setLiveArtifact(null)}
                />
              </div>
              {tabArtifacts.length ? (
                <div className="p-3 space-y-2 border-t border-[var(--border-default)]">
                  <div className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Recent updates</div>
                  {tabArtifacts.slice(0, 6).map((a, idx) => (
                    <ArtifactCard key={`${a.type}-${idx}`} artifact={a} />
                  ))}
                </div>
              ) : null}
            </div>
          ) : tabArtifacts.length ? (
            <div className="p-3 space-y-2">
              {activeEventId ? (
                <p className="text-[10px] font-mono text-[var(--text-tertiary)] truncate">Event · {activeEventId}</p>
              ) : null}
              {tabArtifacts.map((a, idx) => (
                <ArtifactCard key={`${a.type}-${idx}`} artifact={a} />
              ))}
            </div>
          ) : (
            <div className="p-4 text-xs text-[var(--text-tertiary)] space-y-2">
              <p>Live workspace updates as AivoryONE (Beta) creates events, pages, and comms.</p>
              <p className="text-[11px]">
                Try: <span className="text-[var(--text-secondary)]">“Draft a save-the-date EDM”</span>,{" "}
                <span className="text-[var(--text-secondary)]">“Create a VIP ticket tier”</span>, or{" "}
                <span className="text-[var(--text-secondary)]">“Publish the landing page”</span>.
              </p>
            </div>
          )}
        </div>
      </aside>
    </div>
  );
}
